import type {
    ScrollContainer,
    ScrollObservation,
    ShowroomScrollBehavior,
    ShowroomSectionKey
} from '@makemydeal/dr-sales-checkin-types';

import { isShowroomSectionKey } from '../routing/showroomNavigation';
import { ACTIVATION_LINE_RATIO, BOTTOM_EPSILON_PX, SECTION_MIN_HEIGHT_PX, SECTION_SCROLL_MARGIN_PX } from '../constants';

/**
 * The two pure pieces of the continuous-scroll page's scroll handling, kept out of the component
 * tree so they can be reasoned about on their own:
 *
 * - `resolveActiveSection` is arithmetic: given where every section sits, which one is active?
 * - `createScrollContainer` answers "which element actually scrolls?" and adapts the reads to it,
 *   because that is an inner pane on desktop and the document on tablet.
 *
 * `useScrollEngine` (activities-sales-checkin) is the only caller; it owns the effects and the store.
 */

export const resolveActiveSection = ({
    sections,
    viewportTop,
    viewportHeight,
    scrollTop,
    scrollHeight,
    clientHeight
}: ScrollObservation): ShowroomSectionKey | undefined => {
    if (!sections.length) {
        return undefined;
    }

    if (scrollTop + clientHeight >= scrollHeight - BOTTOM_EPSILON_PX) {
        return sections[sections.length - 1].key;
    }

    const activationLine = viewportTop + viewportHeight * ACTIVATION_LINE_RATIO;
    const aboveLine = sections.filter(({ top }) => top <= activationLine);

    return aboveLine.length ? aboveLine[aboveLine.length - 1].key : sections[0].key;
};

/**
 * How much of a section is on screen, as a fraction of whichever is shorter — the section or the
 * viewport. Measuring against the shorter of the two is what keeps the rule usable for a section
 * taller than the viewport: such a section can never show a given share of itself, so covering that
 * share of the viewport counts instead.
 */
export const visibleFractionOf = (
    { top, bottom }: { top: number; bottom: number },
    viewportTop: number,
    viewportHeight: number
): number => {
    const reference = Math.min(bottom - top, viewportHeight);

    if (reference <= 0) {
        return 0;
    }

    const overlap = Math.min(bottom, viewportTop + viewportHeight) - Math.max(top, viewportTop);

    return Math.max(overlap, 0) / reference;
};

const findOverflowingScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let element = node?.parentElement ?? null;

    while (element) {
        const { overflowY } = window.getComputedStyle(element);
        const scrolls = overflowY === 'auto' || overflowY === 'scroll';

        if (scrolls && element.scrollHeight > element.clientHeight) {
            return element;
        }

        element = element.parentElement;
    }

    return null;
};

const fromElement = (element: HTMLElement): ScrollContainer => ({
    root: element,
    eventTarget: element,
    getViewport: () => ({ top: element.getBoundingClientRect().top, height: element.clientHeight }),
    getMetrics: () => ({
        scrollTop: element.scrollTop,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight
    }),
    getOffsetOf: (node) => element.scrollTop + node.getBoundingClientRect().top - element.getBoundingClientRect().top,
    scrollToOffset: (top, behavior) => element.scrollTo({ top, behavior })
});

const fromDocument = (): ScrollContainer => {
    const scroller = document.scrollingElement ?? document.documentElement;

    return {
        root: null,
        eventTarget: window,
        getViewport: () => ({ top: 0, height: window.innerHeight }),
        getMetrics: () => ({
            scrollTop: scroller.scrollTop,
            scrollHeight: scroller.scrollHeight,
            clientHeight: scroller.clientHeight
        }),
        getOffsetOf: (node) => scroller.scrollTop + node.getBoundingClientRect().top,
        scrollToOffset: (top, behavior) => window.scrollTo({ top, behavior })
    };
};

export const createScrollContainer = (stackElement: HTMLElement | null): ScrollContainer => {
    const element = findOverflowingScrollParent(stackElement);

    return element ? fromElement(element) : fromDocument();
};

/**
 * Scrolls the container so `node` sits at its top edge, minus the section's `scroll-margin-top`.
 * We scroll the resolved container by offset rather than calling `node.scrollIntoView()`, because
 * `scrollIntoView` walks *every* ancestor scrollport and can therefore move the host page we are
 * embedded in.
 */
export const scrollToNode = (container: ScrollContainer, node: HTMLElement, behavior: ShowroomScrollBehavior) =>
    container.scrollToOffset(container.getOffsetOf(node) - SECTION_SCROLL_MARGIN_PX, behavior);

const heights = new Map<ShowroomSectionKey, number>();

export const sectionHeightCache = {
    get: (key: ShowroomSectionKey): number | undefined => heights.get(key),
    set: (key: ShowroomSectionKey, height: number): void => {
        heights.set(key, height);
    },
    clear: (): void => heights.clear()
};

export const getPlaceholderHeight = (key: ShowroomSectionKey): number => sectionHeightCache.get(key) ?? SECTION_MIN_HEIGHT_PX[key];

export const sectionKeyOf = (node: Element): ShowroomSectionKey | undefined => {
    const key = (node as HTMLElement).dataset.sectionKey;

    return key && isShowroomSectionKey(key) ? key : undefined;
};
